import MyMapsDialog, { IMyMapsDialog } from "./mymaps-dialog";
import { _ } from "~/i18n";
import { folderCreate, likeMove, setFolderData } from "~/util/providers/mapybox";
import { getMapyboxError, mapyboxNotification } from "~/util/mapybox";
import MyMapsFolderSelectCreate from "../mymaps/folder-select-create";
import { store } from "~/state/store";
import { IMyMapsState } from "~/state/mymaps/interfaces";
import { node } from "~/melown3d/utils";
import { createMyMapsInput } from "../mymaps/input";
import { MAX_TITLE_LEN } from "./const";
import { IMapyBoxItem } from "~/interfaces";

interface IFolder {
	id: string;
	title: string;
}

interface IMyMapsMoveItems extends Pick<IMyMapsDialog, "onCancel" | "onClose"> {
	item?: IMapyBoxItem;
	ids: Array<string>;
	folder?: IFolder;
	moveFolders?: Array<IFolder>;
	isMultiple?: boolean;
	onSave?: (folderId: string, folderTitle: string) => void;
	onError?: () => void;
}

interface IState {
	newFolderTitle: string;
}

export default class MyMapsMoveItems {
	_opts: IMyMapsMoveItems;

	_dialog: MyMapsDialog;

	_folderSelectCreate: MyMapsFolderSelectCreate;

	_mymapsData: IMyMapsState;

	_state: IState;

	constructor(data: IMyMapsMoveItems) {
		this._opts = {
			item: null,
			ids: [],
			folder: null,
			moveFolders: [],
			isMultiple: false,
			onSave: () => {},
			onCancel: () => {},
			onClose: () => {},
			onError: () => {},
			...data,
		};
		this._state = {
			newFolderTitle: "",
		};
		this._mymapsData = {
			...store.getState().mymaps,
		};

		this._build();
	}

	open() {
		this._dialog?.open();
	}

	destroy() {
		this._dialog?.destroy();
	}

	_closeDialog(force?: boolean) {
		this._opts.onClose();
		this._dialog.close(force);
	}

	_build() {
		const saveBtnText = this._mymapsData.hasFolders
			? _("move")
			: _("mymap.createFolderAndMove");
		const dialog = new MyMapsDialog({
			titles: {
				main: [_("mymap.contextMenu.move-to-folder")],
				saveBtn: saveBtnText,
				cancelBtn: _("cancel"),
				throwAwayTitle: _("note.throwAwayNote"),
				throwAwayConfirm: _("detail.content.review2.throwAway"),
			},
			onThrowAwayTest: () => true,
			onSave: () => {
				this._onSave();
			},
			onCancel: () => {
				this._opts.onCancel();
				this._closeDialog(true);
			},
		});
		const mainContent = dialog.contents[0];

		dialog.dom.container.classList.add("move-items-dialog");
		mainContent.classList.add("mymaps-dialog__defContent");
		mainContent.classList.add("mymaps__moveItemsDialog__mainContent");

		if (this._mymapsData.hasFolders) {
			const selectFirstFolder = this._opts.item?.parent_id === this._mymapsData.rootId
				|| (this._opts.isMultiple && this._opts.moveFolders[0]?.id === this._mymapsData.rootId);

			this._folderSelectCreate = new MyMapsFolderSelectCreate({
				folder: this._opts.folder,
				moveFolders: this._opts.moveFolders,
				selectFirstFolder,
				onChange: state => {
					this._dialog.setSaveBtnTitle = state.createNewFolder ? _("mymap.createFolderAndMove") : saveBtnText;
				},
			});

			mainContent.append(this._folderSelectCreate.container);
		} else {
			const createFolder = createMyMapsInput({
				placeholder: _("note.nameFolder"),
				onChange: newFolder => {
					this._state.newFolderTitle = newFolder;
				},
				text: "",
				count: MAX_TITLE_LEN,
			});

			mainContent.append(
				node("p", { className: "firstFolderInfo", textContent: _("mymap.createFirstFolder") }),
				createFolder.container,
			);
		}

		this._dialog = dialog;
	}

	_getFolderState() {
		if (this._folderSelectCreate) {
			return this._folderSelectCreate.state;
		}

		const newFolderTitle = this._state.newFolderTitle.trim();

		return {
			folderId: "",
			createNewFolder: true,
			newFolderTitle: newFolderTitle,
			folderTitle: newFolderTitle,
		};
	}

	async _onSave() {
		const folderState = this._getFolderState();
		let folderId = folderState.folderId || this._mymapsData.rootId;

		this._dialog.error = "";
		this._dialog.loading = true;

		// nova slozka
		if (folderState.createNewFolder) {
			if (folderState.newFolderTitle === "") {
				this._dialog.loading = false;
				this._dialog.error = _("mymap.folders.emptyFolder");

				return;
			}

			try {
				const createData = await folderCreate(folderState.newFolderTitle);

				folderId = createData.id;

				// ulozime - pro pripad override
				if (this._folderSelectCreate) {
					this._folderSelectCreate.overwriteOrigState(folderId);
				}
			} catch (exc) {
				this._dialog.error = getMapyboxError({
					operation: "folder-create",
				});
				this._dialog.loading = false;

				return;
			}
		// neni zmena
		} else if (this._opts.moveFolders.filter(moveFolder => moveFolder.id === folderId).length > 0) {
			this._dialog.loading = false;
			this._dialog.error = _("mymap.moveFolder.same");

			return;
		}

		// presun
		try {
			await likeMove(this._opts.ids, folderId);

			Mapy.ProviderMapyBox.removeFolderFromCache(folderId);
			Mapy.ProviderMapyBox.folderList(folderId).then(() => {
				this._afterMove(folderId, folderState.folderTitle);
			}, () => {
				this._afterMove(folderId, folderState.folderTitle);
			});
		} catch (errData) {
			let duplicateLink = false;

			if ("errors" in errData && errData.errors.length > 0) {
				errData.errors.forEach((errItem: { status: number; }) => {
					if (errItem.status === 409) {
						duplicateLink = true;
					}
				});
			}

			if (duplicateLink) {
				mapyboxNotification({
					operation: "move-track-link-conflict",
					folderId,
					folderTitle: folderState.folderTitle,
				});

				this._sendAnalytics();
				this._successCloseDialog(folderId, folderState);

				return;
			}

			this._dialog.error = getMapyboxError({
				item: this._opts.item,
				operation: "move-items",
				moveItemsLen: this._opts.isMultiple
					? this._opts.ids.length
					: 0,
			});
			this._dialog.loading = false;
			this._opts.onError();

			return;
		}

		this._successCloseDialog(folderId, folderState);
	}

	_successCloseDialog(folderId: string, folderState: ReturnType<typeof this._getFolderState>) {
		setFolderData({
			id: folderId,
			title: folderState.folderTitle,
		});

		this._opts.onSave(folderId, folderState.folderTitle);
		this._closeDialog(true);
	}

	_afterMove(folderId: string, folderName: string) {
		Mapy.getComponent("places").listRefreshData();
		Mapy.getComponent("places").notifyLikeUpdate();
		mapyboxNotification({
			operation: "move-items",
			item: this._opts.item,
			folderId,
			folderTitle: folderName,
			moveItemsLen: this._opts.isMultiple
				? this._opts.ids.length
				: 0,
		});
		this._sendAnalytics();
	}

	_sendAnalytics() {
		Mapy.getComponent("logger").log("my_maps_items_move", {
			type: "move",
			...this._opts.ids.length > 1
				? {
					length: this._opts.ids.length,
				}
				: {},
		});
	}
}
